import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit, RotateCcw, KeyRound, Copy as CopyIcon, Check as CheckIcon, Umbrella, Trash2, AlertTriangle } from "lucide-react";
import BenefitsDialog from "./BenefitsDialog";

const PAY_METHODS = [
  { value: "hourly", label: "Hourly only" },
  { value: "commission", label: "Commission only" },
  { value: "higher_of", label: "Higher of hourly/commission" },
  { value: "hourly_plus_commission", label: "Hourly + commission" },
];

export default function AdminEmployees() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  // Whether the tenant has Benefits & PTO enabled globally — if so, show the
  // per-row Benefits button so admins can configure each employee. Read once
  // on mount from /api/settings.
  const [benefitsGlobalOn, setBenefitsGlobalOn] = useState(false);
  const [benefitsTarget, setBenefitsTarget] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/settings");
        setBenefitsGlobalOn(!!s.data?.benefits_enabled);
      } catch { /* keep default off */ }
    })();
  }, []);

  // Reset-password dialog state. Lives next to the existing edit dialog so it
  // doesn't bloat the row-action button strip.
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { password, email }
  const [resetCopied, setResetCopied] = useState(false);

  const openResetPassword = (emp) => {
    setResetTarget(emp);
    setResetPwd("");
    setResetResult(null);
    setResetCopied(false);
  };
  const closeResetPassword = () => {
    setResetTarget(null);
    setResetPwd("");
    setResetResult(null);
    setResetCopied(false);
  };
  const submitResetPassword = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const { data } = await api.post(
        `/admin/employees/${resetTarget.id}/reset-password`,
        { new_password: resetPwd.trim() || null }
      );
      setResetResult({ password: data.new_password, email: data.email });
      toast.success("Password reset");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResetBusy(false);
    }
  };
  const copyResetPwd = async () => {
    if (!resetResult?.password) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      setResetCopied(true);
      setTimeout(() => setResetCopied(false), 1500);
    } catch {
      // Some browsers (esp. older Safari) deny clipboard without user gesture
      // even though this IS a gesture — fail silently, the password is on screen.
    }
  };

  // Delete-employee confirmation state. Two-step to avoid accidental removals:
  // (1) click "Delete Employee" in the edit dialog, (2) type DELETE + confirm.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const openDelete = (emp) => {
    setDeleteTarget(emp);
    setDeleteConfirmText("");
  };
  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmText("");
    setDeleteBusy(false);
  };
  const submitDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText !== "DELETE") return;
    setDeleteBusy(true);
    try {
      await api.delete(`/employees/${deleteTarget.id}`);
      toast.success(`${deleteTarget.first_name} ${deleteTarget.last_name} deleted.`);
      closeDelete();
      // Also close the outer edit dialog since the record no longer exists
      setOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  function empty() {
    return {
      first_name: "", last_name: "", email: "", phone: "",
      active: true, role: "employee", worker_type: "w2",
      hourly_rate: 0, overtime_multiplier: 1.5, commission_percentage: 0,
      tax_withholding_enabled: true, default_pay_method: "higher_of",
      is_lead_technician: false,
      pay_structures: {},
      password: "",
    };
  }

  const load = async () => {
    const { data } = await api.get("/employees");
    setItems(data);
  };
  // Industry gates whether the HVAC pay-structure block renders inside
  // the Employee modal. Fetched once on mount.
  const [industry, setIndustry] = useState("other");
  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await api.get("/companies/me/industry");
        setIndustry(data.industry || "other");
      } catch {}
    })();
  }, []);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...empty(), ...e, password: "" }); setOpen(true); };

  // Admin "Reset Day" — wipe everything an employee has for a given date.
  // Default is today (in company local timezone, computed server-side).
  const resetDay = async (emp) => {
    const today = new Date();
    const tzDate = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric", month: "2-digit", day: "2-digit",
    }).format(today); // YYYY-MM-DD in Eastern time
    const day = prompt(
      `Reset which day for ${emp.first_name} ${emp.last_name}?\n` +
      `Format YYYY-MM-DD. Defaults to today (${tzDate}).\n\n` +
      `This will permanently delete time entries, lunch, jobs, shop time, ` +
      `truck inspection, and any unfinalized submission for that day.`,
      tzDate
    );
    if (!day) return;
    const reason = prompt(`Reason (logged for audit):`, "Bad data from prior day showing on new day");
    if (reason === null) return;
    if (!confirm(`Really wipe all of ${emp.first_name} ${emp.last_name}'s data for ${day}?`)) return;
    try {
      const { data } = await api.post("/admin/reset-day", {
        user_id: emp.id, date: day, confirm: true, reason,
      });
      const counts = data?.deleted || {};
      const summary = Object.entries(counts).filter(([_, n]) => n > 0).map(([k, n]) => `${k}: ${n}`).join(", ") || "nothing to delete";
      toast.success(`Reset complete — ${summary}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Generate a strong random temp password. We avoid a hardcoded default
  // (previous "ChangeMe@123") so every new employee gets a unique credential.
  // The admin sees the value in a toast and can copy/share it with the
  // employee, who is expected to change it on first login.
  const generateTempPassword = () => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // no I/O
    const lower = "abcdefghjkmnpqrstuvwxyz";  // no i/l/o
    const digit = "23456789";                 // no 0/1
    const sym = "!@#$%^&*";
    const all = upper + lower + digit + sym;
    const rand = (s) => s[Math.floor(Math.random() * s.length)];
    // Guarantee at least one from each class so it passes strength policies.
    const seed = [rand(upper), rand(lower), rand(digit), rand(sym)];
    for (let i = 0; i < 10; i++) seed.push(rand(all));
    // Fisher-Yates shuffle
    for (let i = seed.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [seed[i], seed[j]] = [seed[j], seed[i]];
    }
    return seed.join("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        hourly_rate: Number(form.hourly_rate) || 0,
        overtime_multiplier: Number(form.overtime_multiplier) || 1.5,
        commission_percentage: Number(form.commission_percentage) || 0,
      };
      if (!payload.password) delete payload.password;
      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        // For new employees: use admin-entered password if provided, else
        // generate a unique random temp password and surface it to the admin
        // so they can share it with the new hire. No hardcoded defaults.
        let tempPassword = null;
        if (!payload.password) {
          tempPassword = generateTempPassword();
          payload.password = tempPassword;
        }
        await api.post("/employees", payload);
        if (tempPassword) {
          toast.success(`Employee created. Temp password: ${tempPassword} — share it with them; they can change it after signing in.`, { duration: 20000 });
        } else {
          toast.success("Employee created");
        }
        // Facebook Pixel — fire Activated the FIRST time an employee is
        // added to the tenant. The signal that the owner actually did
        // the first real setup step inside the app. We gate on the
        // pre-save employee count to ensure it only fires once ever.
        try {
          if (items.length === 0 && typeof window !== "undefined" && typeof window.fbq === "function") {
            window.fbq("trackCustom", "Activated", { first_employee: true });
          }
        } catch {}
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const filtered = items.filter((e) => {
    if (roleFilter !== "all" && e.role !== roleFilter) return false;
    if (activeFilter === "active" && !e.active) return false;
    if (activeFilter === "inactive" && e.active) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${e.first_name || ""} ${e.last_name || ""} ${e.email || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="admin-employees-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Team Members</h1>
          <p className="text-sm text-slate-500 mt-1">Manage employees and admins — pay rates, roles, status.</p>
        </div>
        <Button onClick={openNew} className="bg-sky-600 hover:bg-sky-700" data-testid="add-employee-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Person
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
          data-testid="employees-search-input"
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-44" data-testid="role-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="employee">Employees</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="w-44" data-testid="active-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Active &amp; inactive</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="inactive">Inactive only</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 ml-auto">{filtered.length} of {items.length}</p>
      </div>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-right px-4 py-3 font-semibold">Rate</th>
                <th className="text-right px-4 py-3 font-semibold">Comm %</th>
                <th className="text-center px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  className="group border-b border-slate-100 hover:bg-sky-50/40 cursor-pointer transition-colors"
                  data-testid={`employee-row-${e.id}`}
                  onClick={() => openEdit(e)}
                >
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    <button
                      type="button"
                      className="text-left text-sky-700 hover:underline focus:outline-none"
                      data-testid={`name-link-${e.id}`}
                      onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}
                    >
                      {e.first_name} {e.last_name}
                    </button>
                    {e.is_lead_technician && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700" title="Lead Technician / Driver">Lead</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{e.email}</td>
                  <td className="px-4 py-3">
                    <Badge className={e.role === "admin" ? "bg-sky-100 text-sky-700 hover:bg-sky-100 capitalize" : "bg-slate-100 text-slate-700 hover:bg-slate-100 capitalize"}>
                      {e.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 uppercase">{e.worker_type}</td>
                  <td className="px-4 py-3 text-right">${(e.hourly_rate || 0).toFixed(2)}/h</td>
                  <td className="px-4 py-3 text-right">{e.commission_percentage || 0}%</td>
                  <td className="px-4 py-3 text-center">
                    <Badge className={e.active ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-slate-200 text-slate-700"}>{e.active ? "Active" : "Inactive"}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-sky-600 text-sky-700 hover:bg-sky-50 hover:text-sky-800"
                        onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}
                        data-testid={`edit-employee-${e.id}-btn`}
                      >
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                        onClick={(ev) => { ev.stopPropagation(); resetDay(e); }}
                        data-testid={`reset-day-${e.id}-btn`}
                        title="Wipe a day's data for this employee"
                      >
                        <RotateCcw className="h-4 w-4 mr-1" /> Reset Day
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                        onClick={(ev) => { ev.stopPropagation(); openResetPassword(e); }}
                        data-testid={`reset-password-${e.id}-btn`}
                        title="Set a new password for this person"
                      >
                        <KeyRound className="h-4 w-4 mr-1" /> Reset Password
                      </Button>
                      {benefitsGlobalOn && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-sky-300 text-sky-700 hover:bg-sky-50 hover:text-sky-800"
                          onClick={(ev) => { ev.stopPropagation(); setBenefitsTarget(e); }}
                          data-testid={`benefits-${e.id}-btn`}
                          title="Manage vacation, sick, holidays, and other PTO"
                        >
                          <Umbrella className="h-4 w-4 mr-1" /> Benefits
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-500">No team members match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.role === "admin" ? "Admin" : "Employee"} — ${editing.first_name} ${editing.last_name}` : "New Team Member"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-fname-input" /></Field>
            <Field label="Last Name"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-lname-input" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="emp-email-input" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label={editing ? "Reset Password (leave blank to keep)" : "Initial Password (leave blank to auto-generate)"}><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "" : "Leave blank to auto-generate"} data-testid="emp-pwd-input" /></Field>
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="emp-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Worker Type">
              <Select value={form.worker_type} onValueChange={(v) => setForm({ ...form, worker_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="w2">W2 Payroll</SelectItem>
                  <SelectItem value="1099">1099 Contractor</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Default Pay Method">
              <Select value={form.default_pay_method} onValueChange={(v) => setForm({ ...form, default_pay_method: v })}>
                <SelectTrigger data-testid="emp-paymethod-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAY_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Hourly Rate ($/h)"><Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="emp-rate-input" /></Field>
            <Field label="Overtime Multiplier"><Input type="number" step="0.1" value={form.overtime_multiplier} onChange={(e) => setForm({ ...form, overtime_multiplier: e.target.value })} /></Field>
            <Field label="Commission %"><Input type="number" step="0.1" value={form.commission_percentage} onChange={(e) => setForm({ ...form, commission_percentage: e.target.value })} data-testid="emp-comm-input" /></Field>
            <Field label="Tax Withholding">
              <div className="flex items-center gap-2 h-10">
                <Switch checked={form.tax_withholding_enabled} onCheckedChange={(v) => setForm({ ...form, tax_withholding_enabled: v })} />
                <span className="text-sm">{form.tax_withholding_enabled ? "Enabled" : "Disabled"}</span>
              </div>
            </Field>
            <Field label="Active">
              <div className="flex items-center gap-2 h-10">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <span className="text-sm">{form.active ? "Active" : "Inactive"}</span>
              </div>
            </Field>
            <Field label="Lead Technician / Driver">
              <div className="flex items-center gap-2 h-10">
                <Switch
                  checked={!!form.is_lead_technician}
                  onCheckedChange={(v) => setForm({ ...form, is_lead_technician: v })}
                  data-testid="emp-lead-tech-switch"
                />
                <span className="text-sm">{form.is_lead_technician ? "Required to complete truck pre-inspection" : "Not required"}</span>
              </div>
            </Field>
          </div>

          {industry === "hvac" && (
            <HvacPayStructuresEditor
              value={form.pay_structures || {}}
              onChange={(v) => setForm({ ...form, pay_structures: v })}
            />
          )}

          <div className="flex gap-2 pt-3">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={busy} className="flex-1 bg-sky-600 hover:bg-sky-700" data-testid="save-employee-btn">{busy ? "Saving..." : "Save"}</Button>
          </div>

          {editing && editing.id !== currentUser?.id && (
            <div className="mt-6 pt-5 border-t border-red-200">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-extrabold text-red-900">Danger zone</p>
                  <p className="text-xs text-red-800 mt-1 leading-relaxed">
                    Delete this employee. Their historical time cards, paystubs, and audit
                    entries are kept for tax/labor compliance, but they will disappear from
                    Team Members and won&apos;t be able to log in. Their email is freed
                    up for reuse.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => openDelete(editing)}
                    className="mt-3 border-red-300 bg-white text-red-700 hover:bg-red-100 hover:text-red-800"
                    data-testid="delete-employee-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Delete Employee
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — user must type DELETE to unlock the button */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) closeDelete(); }}>
        <DialogContent className="max-w-md" data-testid="delete-employee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-900">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete {deleteTarget?.first_name} {deleteTarget?.last_name}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900 leading-relaxed">
              This will remove <b>{deleteTarget?.first_name} {deleteTarget?.last_name}</b> ({deleteTarget?.email}) from your Team Members and revoke their login access. Historical time cards, paystubs, and audit rows are <b>preserved</b> for tax/labor compliance.
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Type <span className="font-mono font-bold text-red-700">DELETE</span> to confirm
              </Label>
              <Input
                type="text"
                autoComplete="off"
                autoFocus
                className="mt-1 font-mono"
                placeholder="DELETE"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                data-testid="delete-confirm-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={closeDelete} data-testid="delete-cancel-btn">
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                onClick={submitDelete}
                disabled={deleteBusy || deleteConfirmText !== "DELETE"}
                data-testid="delete-confirm-btn"
              >
                {deleteBusy ? "Deleting…" : "Delete permanently"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) closeResetPassword(); }}>
        <DialogContent className="max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>
              Reset password — {resetTarget?.first_name} {resetTarget?.last_name}
            </DialogTitle>
          </DialogHeader>
          {!resetResult ? (
            <div className="space-y-4 pt-1">
              <p className="text-sm text-slate-600">
                Set a new password for <span className="font-semibold">{resetTarget?.email}</span>.
                Leave blank to have the server generate a strong random one — you can read it to them or paste it into a message.
              </p>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                  New password (optional)
                </Label>
                <Input
                  type="text"
                  autoComplete="new-password"
                  className="mt-1"
                  placeholder="e.g. Welcome2026!  (or leave blank to auto-generate)"
                  value={resetPwd}
                  onChange={(e) => setResetPwd(e.target.value)}
                  data-testid="reset-password-input"
                />
                <p className="text-xs text-slate-500 mt-1">Min 8 characters.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={closeResetPassword} data-testid="reset-password-cancel-btn">
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-sky-600 hover:bg-sky-700"
                  onClick={submitResetPassword}
                  disabled={resetBusy}
                  data-testid="reset-password-submit-btn"
                >
                  {resetBusy ? "Resetting…" : "Reset password"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-1" data-testid="reset-password-result">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm font-semibold text-emerald-900">Password updated.</p>
                <p className="text-xs text-emerald-800 mt-1">
                  Share this with {resetTarget?.first_name}. We don&apos;t store it in plain text — once you close this dialog, you can&apos;t see it again.
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                  Temporary password
                </Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    readOnly
                    value={resetResult.password}
                    className="font-mono"
                    data-testid="reset-password-display"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    onClick={copyResetPwd}
                    className="shrink-0"
                    data-testid="reset-password-copy-btn"
                  >
                    {resetCopied ? <CheckIcon className="h-4 w-4 mr-1 text-emerald-600" /> : <CopyIcon className="h-4 w-4 mr-1" />}
                    {resetCopied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
              <Button
                className="w-full bg-sky-600 hover:bg-sky-700"
                onClick={closeResetPassword}
                data-testid="reset-password-done-btn"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Benefits & PTO */}
      <BenefitsDialog
        open={!!benefitsTarget}
        employee={benefitsTarget}
        onOpenChange={(o) => { if (!o) setBenefitsTarget(null); }}
        onSaved={load}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


/* ─────────────────────── HVAC pay structures ─────────────────────── */

/** Compact 4-structure editor rendered inside the Employee modal on
 *  HVAC tenants. Value shape:
 *    {
 *      flat_rate:        { enabled: bool },
 *      commission:       { enabled: bool, bonus_tier_amount: number|null },
 *      hourly_spiffs:    { enabled: bool },
 *      agreement_bonus:  { enabled: bool, amount: number|null },
 *    }
 *  Shape is intentionally loose — the backend tolerates missing fields.
 */
function HvacPayStructuresEditor({ value, onChange }) {
  const v = value || {};
  const toggle = (key) => (checked) => {
    onChange({ ...v, [key]: { ...(v[key] || {}), enabled: !!checked } });
  };
  const setField = (key, field) => (ev) => {
    const raw = ev?.target ? ev.target.value : ev;
    const num = raw === "" ? null : parseFloat(raw);
    onChange({ ...v, [key]: { ...(v[key] || {}), [field]: isNaN(num) ? null : num } });
  };

  const row = (key, title, desc, extra = null) => (
    <div className={`rounded-xl border p-3 ${v[key]?.enabled ? "border-sky-300 bg-sky-50/60" : "border-slate-200 bg-white"}`} data-testid={`hvac-struct-${key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-sm text-slate-900">{title}</p>
          <p className="text-xs text-slate-600 mt-0.5">{desc}</p>
        </div>
        <Switch
          checked={!!v[key]?.enabled}
          onCheckedChange={toggle(key)}
          data-testid={`hvac-struct-${key}-toggle`}
        />
      </div>
      {v[key]?.enabled && extra}
    </div>
  );

  return (
    <div className="mt-5 pt-5 border-t border-slate-100 space-y-3" data-testid="hvac-pay-structures">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-900">HVAC pay structures (mix &amp; match)</p>
        <span className="text-[10px] uppercase tracking-widest text-sky-700 font-extrabold">HVAC</span>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Turn on any combination. These layer on top of the base hourly/commission settings above.
      </p>
      {row("flat_rate", "Flat-rate per job", "Fixed pay per job type from the Industry → Flat-rate rate card. Per-entry overrides allowed on each job.")}
      {row(
        "commission",
        "Commission on sold jobs",
        "Uses the Commission % field above. Optionally add a flat bonus tier for higher-priced systems.",
        <div className="mt-2 grid grid-cols-[auto_120px] items-center gap-2">
          <Label className="text-xs text-slate-600">Bonus tier amount ($)</Label>
          <Input
            type="number" step="0.01"
            value={v.commission?.bonus_tier_amount ?? ""}
            onChange={setField("commission", "bonus_tier_amount")}
            data-testid="hvac-commission-bonus-input"
            className="h-8"
          />
        </div>,
      )}
      {row("hourly_spiffs", "Hourly + spiffs & bonuses", "Base hourly from the Hourly Rate field above. Per-item spiffs are entered on each job (add-on, IAQ product, thermostat, etc.).")}
      {row(
        "agreement_bonus",
        "Maintenance-agreement bonus",
        "Flat bonus every time this tech sells a maintenance agreement on a job.",
        <div className="mt-2 grid grid-cols-[auto_120px] items-center gap-2">
          <Label className="text-xs text-slate-600">Bonus per agreement ($)</Label>
          <Input
            type="number" step="0.01"
            value={v.agreement_bonus?.amount ?? ""}
            onChange={setField("agreement_bonus", "amount")}
            data-testid="hvac-agreement-amount-input"
            className="h-8"
          />
        </div>,
      )}
    </div>
  );
}
